/**
 * ProductsHeader - Page header with sync controls.
 */
import { Package, RefreshCw, ChevronDown, RotateCcw, CheckSquare, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Button, Badge, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/v2/ui'
import type { SyncStatusType } from '@/types/products'

interface ProductsHeaderProps {
  count: number
  syncStatus: SyncStatusType
  isSyncing: boolean
  isResetting: boolean
  isSelecting: boolean
  onStartSync: (fullSync: boolean) => void
  onResetSync: () => void
  onToggleSelect: () => void
}

function SyncBadge({ status }: { status: SyncStatusType }) {
  const { t } = useTranslation()
  if (status === 'running')
    return <Badge variant="primary"><Loader2 className="h-3 w-3 mr-1 animate-spin" />{t('products.sync_badge_running')}</Badge>
  if (status === 'error') return <Badge variant="error">{t('products.sync_badge_error')}</Badge>
  return <Badge variant="secondary">{t('products.sync_badge_ready')}</Badge>
}

export function ProductsHeader({
  count, syncStatus, isSyncing, isResetting, isSelecting,
  onStartSync, onResetSync, onToggleSelect,
}: ProductsHeaderProps) {
  const { t } = useTranslation()
  return (
    <header className="flex items-center justify-between mb-8 flex-wrap gap-4">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
            <Package className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">{t('products.title')}</h1>
        </div>
        <p className="text-neutral-600 dark:text-neutral-400">{t('products.products_synced', { count })}</p>
      </div>
      <div className="flex gap-4 items-center">
        <Button variant={isSelecting ? 'primary' : 'outline'} onClick={onToggleSelect}>
          <CheckSquare className="h-4 w-4 mr-2" />{isSelecting ? t('common.cancel') : t('common.select')}
        </Button>
        <div className="text-right">
          <div className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">{t('products.sync_status_label')}</div>
          <SyncBadge status={syncStatus} />
        </div>
        <div className="flex gap-2">
          {(syncStatus === 'running' || syncStatus === 'error') && (
            <Button variant="secondary" onClick={onResetSync} disabled={isResetting} title={t('products.sync_reset_title')}>
              <RotateCcw className="h-4 w-4 mr-2" />Reset
            </Button>
          )}
          <Button variant="primary" onClick={() => onStartSync(false)} disabled={isSyncing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? t('products.sync_running') : t('products.sync_start')}
          </Button>
          <SyncDropdown isSyncing={isSyncing} onStartSync={onStartSync} />
        </div>
      </div>
    </header>
  )
}

function SyncDropdown({ isSyncing, onStartSync }: { isSyncing: boolean; onStartSync: (full: boolean) => void }) {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="primary" disabled={isSyncing}><ChevronDown className="h-4 w-4" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={() => onStartSync(false)}>
          <RefreshCw className="h-4 w-4 mr-2" />
          <div>
            <div className="font-medium">{t('products.sync_incremental')}</div>
            <div className="text-sm text-neutral-500">{t('products.sync_incremental_desc')}</div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onStartSync(true)}>
          <RefreshCw className="h-4 w-4 mr-2" />
          <div>
            <div className="font-medium">{t('products.sync_full')}</div>
            <div className="text-sm text-neutral-500">{t('products.sync_full_desc')}</div>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
